import { useState, useCallback, useEffect, useRef } from "react";
import { questionService } from "../services/QuestionService";
import { isAnswerCorrect } from "./useAnswerValidation";

// Create audio elements with public URLs
const correctAudio = new Audio('/sounds/correct.mp3');
const incorrectAudio = new Audio('/sounds/incorrect.mp3');

// Preload audio files
correctAudio.load();
incorrectAudio.load();

export const useQuizState = (quizId) => {
  const [quizData, setQuizData] = useState(null);
  const [currentQuestionIndex, setCurrentQuestionIndex] = useState(0);
  const [score, setScore] = useState(0);
  const [showScore, setShowScore] = useState(false);
  const [userAnswers, setUserAnswers] = useState({});
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState(null);
  const [checkedQuestions, setCheckedQuestions] = useState(new Set());
  
  // Create a ref to store the current question for use in callbacks
  const currentQuestionRef = useRef(null);
  
  const currentQuestion = quizData?.questions?.[currentQuestionIndex];
  
  // Update the ref when currentQuestion changes
  useEffect(() => {
    currentQuestionRef.current = currentQuestion;
  }, [currentQuestion]);
  
  // Check if the current question has been checked
  const isAnswerChecked = currentQuestion ? checkedQuestions.has(currentQuestion.id) : false;

  // Fisher-Yates shuffle algorithm
  const shuffleArray = (array) => {
    const newArray = [...array];
    for (let i = newArray.length - 1; i > 0; i--) {
      const j = Math.floor(Math.random() * (i + 1));
      [newArray[i], newArray[j]] = [newArray[j], newArray[i]];
    }
    return newArray;
  };

  // Load quiz data
  useEffect(() => {
    const loadQuizData = async () => {
      setIsLoading(true);
      setError(null);
      try {
        console.log(`Loading quiz configuration for: ${quizId}`);
        
        // 1. Load the quizzes configuration
        const configResponse = await fetch('/quiz-data/quizzes.json');
        if (!configResponse.ok) {
          throw new Error(`Failed to load quiz configuration: ${configResponse.statusText}`);
        }
        
        const quizzes = await configResponse.json();
        const quizConfig = quizzes.find(q => q.id === quizId);
        
        if (!quizConfig) {
          throw new Error(`Quiz with ID "${quizId}" not found in quizzes.json`);
        }
        
        console.log('Quiz config:', quizConfig);

        // 2. Load questions based on config
        console.log('Loading questions...');
        const questions = await questionService.loadQuestions(quizConfig);
        console.log(`Loaded ${questions?.length || 0} questions`);
        
        if (!questions?.length) {
          throw new Error(`No questions available for quiz "${quizConfig.title}"`);
        }

        // 3. Shuffle questions if needed
        const shuffledQuestions = questions.length > 1 ? shuffleArray(questions) : questions;
        
        setQuizData({
          ...quizConfig,
          questions: shuffledQuestions
        });
        
      } catch (err) {
        console.error("Error loading quiz data:", err);
        const errorMessage = err.message || "Failed to load the quiz. Please try again later.";
        console.error('Error details:', {
          quizId,
          message: err.message,
          stack: err.stack
        });
        setError(errorMessage);
      } finally {
        setIsLoading(false);
      }
    };

    loadQuizData();
  }, [quizId]);

  // No need for a separate checkAnswerCorrect function, we'll use isAnswerCorrect directly

  // Update score when answers or checked questions change
  useEffect(() => {
    if (!quizData?.questions) return;

    console.log('Recalculating score...');
    console.log('Checked questions:', Array.from(checkedQuestions));
    console.log('User answers:', Object.keys(userAnswers));

    // Calculate score based on all checked questions
    const newScore = quizData.questions.reduce((total, question) => {
      const isChecked = checkedQuestions.has(question.id);
      const hasAnswer = userAnswers[question.id] !== undefined;
      
      if (isChecked && hasAnswer) {
        const answerCorrect = isAnswerCorrect(
          question,
          userAnswers[question.id]
        );
        console.log(`Question ${question.id}:`, 
          `Checked: ${isChecked},`,
          `Has Answer: ${hasAnswer},`,
          `Correct: ${answerCorrect}`);
        return answerCorrect ? total + 1 : total;
      }
      return total;
    }, 0);

    console.log('New score:', newScore);
    
    // Only update score if it has actually changed
    setScore(prevScore => {
      if (newScore !== prevScore) {
        console.log('Score updated from', prevScore, 'to', newScore);
        return newScore;
      }
      return prevScore;
    });
  }, [userAnswers, checkedQuestions, quizData]);

  const handleAnswer = useCallback((questionId, answer, questionType) => {
    setUserAnswers((prev) => {
      // Only update if the answer has actually changed
      const currentAnswer = prev[questionId];
      
      // Normalize the answer format for match questions
      let normalizedAnswer = answer;
      if (questionType === 'match' && Array.isArray(answer)) {
        // Ensure each match has consistent property names
        normalizedAnswer = answer.map(match => {
          if (!match) return null;
          // Convert to { leftId, rightId } format if needed
          if (match.left !== undefined && match.right !== undefined) {
            return { leftId: match.left, rightId: match.right };
          }
          return match;
        }).filter(Boolean); // Remove any null/undefined values
      }
      
      if (JSON.stringify(currentAnswer) === JSON.stringify(normalizedAnswer)) {
        return prev;
      }
      
      console.log('Updating answer for question:', questionId, 'New answer:', normalizedAnswer);
      return {
        ...prev,
        [questionId]: normalizedAnswer,
      };
    });
  }, []);

  const handleCheckAnswer = useCallback(() => {
    const currentQ = currentQuestionRef.current;
    if (!currentQ) {
      console.log('No current question');
      return;
    }

    // Get the user's answer for the current question
    const userAnswer = userAnswers[currentQ.id];
    console.log('Checking answer for question:', currentQ.id, 'Answer:', userAnswer);
    
    // If there's no answer, don't proceed
    if (userAnswer === undefined || 
        (Array.isArray(userAnswer) && userAnswer.length === 0)) {
      console.log('No answer provided for question:', currentQ.id);
      return;
    }

    // Make sure the answer is in the correct format
    let normalizedAnswer = userAnswer;
    if (currentQ.type === 'multiple' && !currentQ.multiple) {
      // For single-select multiple choice, ensure it's an array with one item
      normalizedAnswer = Array.isArray(userAnswer) ? userAnswer : [userAnswer];
    }

    // Check if the answer is correct using the imported validation directly
    const answerIsCorrect = isAnswerCorrect(currentQ, normalizedAnswer);
    console.log('Answer is correct:', answerIsCorrect);
    
    // Update the answer with the normalized version if needed
    // Update answers if they've changed
    if (JSON.stringify(normalizedAnswer) !== JSON.stringify(userAnswer)) {
      console.log('Updating normalized answer for question:', currentQ.id);
      setUserAnswers(prev => ({
        ...prev,
        [currentQ.id]: normalizedAnswer
      }));
    }
    
    // Mark this question as checked
    console.log('Marking question as checked:', currentQ.id);
    setCheckedQuestions(prev => {
      const newSet = new Set(prev);
      newSet.add(currentQ.id);
      console.log('New checked questions:', Array.from(newSet));
      return newSet;
    });
    
    // Play sound based on whether the answer is correct
    if (answerIsCorrect) {
      console.log('Playing correct sound');
      correctAudio
        .play()
        .then(() => console.log('Correct sound played successfully'))
        .catch((e) => console.error("Error playing correct sound:", e));
    } else {
      console.log('Playing incorrect sound');
      incorrectAudio
        .play()
        .then(() => console.log('Incorrect sound played successfully'))
        .catch((e) => console.error("Error playing incorrect sound:", e));
    }
  }, [userAnswers]);

  const handleNext = useCallback(() => {
    if (!currentQuestion) return;

    // Don't clear the checked state, just move to the next question
    if (currentQuestionIndex < (quizData?.questions?.length || 0) - 1) {
      const nextIndex = currentQuestionIndex + 1;
      const nextQuestion = quizData?.questions?.[nextIndex];
      
      // Update the ref and state in a single batch
      currentQuestionRef.current = nextQuestion || null;
      setCurrentQuestionIndex(nextIndex);
      
      // Reset the checked state for the new question
      if (nextQuestion) {
        setCheckedQuestions(prev => {
          const newSet = new Set(prev);
          newSet.delete(nextQuestion.id);
          return newSet;
        });
      }
    } else {
      setShowScore(true);
    }
  }, [currentQuestion, currentQuestionIndex, quizData?.questions]);

  return {
    quizData,
    currentQuestion,
    currentQuestionIndex,
    score,
    showScore,
    userAnswers,
    isLoading,
    error,
    isAnswerChecked, // Removed the function call since it's now a boolean
    handleAnswer,
    handleCheckAnswer,
    handleNext,
    setShowScore,
  };
};
